import React, { useState } from "react";
import { format } from "date-fns";
import { FaClipboardList, FaPaperclip, FaClock, FaCalendarAlt } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import Loader from "../../../components/Loader/Loader";
import { useStudentSession } from "../context/StudentSessionContext";
import useStudentHomework from "./hooks/useStudentHomework";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";
import "./StudentHomework.css";

// Confirmed against the real backend (2026-10-22): homework items use
// homework_title / homework_description (HTML, school-authored) /
// homework_attachment, with nested subject.name and teacher.name objects —
// not the generic title/description/attachment_url names guessed earlier.
// Fallbacks are kept as a safety net only.
const getSubjectLabel = (item) => item?.subject?.name || item?.subject_name || "Homework";

const getTitle = (item) => item?.homework_title || item?.title || item?.name || "";

const getDescription = (item) => item?.homework_description || item?.description || item?.details || "";

const getAttachmentUrl = (item) =>
  item?.homework_attachment || item?.attachment_url || item?.attachment || item?.file_url || null;

const getTeacherName = (item) => item?.teacher?.name || "";

const formatDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

// Weekly homework carries its own start_date/end_date range (the dateRangeKey
// tag from useStudentHomework's flattening); daily homework has neither, so
// it's just whatever date the toolbar above is already filtered to.
const formatDueLabel = (item) => {
  const { start_date: start, end_date: end } = item || {};
  if (start && end && start !== end) return `${formatDate(start)} – ${formatDate(end)}`;
  return formatDate(start || end || item?.assigned_date);
};

const getDueReferenceDate = (item) => item?.end_date || item?.assigned_date || item?.start_date || null;

// Colors the due-date badge by how urgent it is, instead of always the same
// warning orange — overdue reads as danger, today as warning, upcoming as a
// calmer info/neutral tone.
const getDueDateTone = (value) => {
  if (!value) return "warning";
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return "warning";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  if (due < today) return "danger";
  if (due.getTime() === today.getTime()) return "warning";
  return "info";
};

const StudentHomework = () => {
  const { student } = useStudentSession();
  // Confirmed against the real backend (2026-10-22): student/homework needs
  // a program_id — the student's own current class, from student/details'
  // student_program.program_id.
  const programId = student?.student_program?.program_id;
  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const { homeworkItems, isLoading } = useStudentHomework(programId, selectedDate);

  return (
    <div className="student-homework">
      <h5 className="sp-section-title mb-3">My Homework</h5>

      <div className="student-homework-toolbar sp-card">
        <span className="sp-icon-tile">
          <FaCalendarAlt />
        </span>
        <div className="student-homework-toolbar-field">
          <label htmlFor="homework-date" className="student-homework-date-label">
            Filter by Date
          </label>
          <input
            id="homework-date"
            type="date"
            className="student-homework-date-input"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <Loader />
      ) : homeworkItems.length === 0 ? (
        <div className="sp-empty-state">
          <span className="sp-icon-tile" data-tone="secondary">
            <FaClipboardList size={18} />
          </span>
          <p className="mb-0">There is no homework added for this date</p>
        </div>
      ) : (
        <div className="sp-card-grid">
        {homeworkItems.map((item, index) => {
          const title = getTitle(item);
          const description = getDescription(item);
          const dueDateLabel = formatDueLabel(item);
          const dueReferenceDate = getDueReferenceDate(item);
          const attachmentUrl = getAttachmentUrl(item);
          const subjectLabel = getSubjectLabel(item);
          const teacherName = getTeacherName(item);

          return (
            <div className="sp-card sp-card--hoverable" key={item?.id || index}>
              <div className="student-homework-card-head">
                <div className="student-homework-card-head-main">
                  <span className="sp-icon-tile">
                    <FaClipboardList />
                  </span>
                  <h3 className="student-homework-subject">{subjectLabel}</h3>
                </div>
                {dueDateLabel && (
                  <span className="student-homework-due" data-tone={getDueDateTone(dueReferenceDate)}>
                    <FaClock size={11} /> Due {dueDateLabel}
                  </span>
                )}
              </div>
              {title && <p className="student-homework-title">{title}</p>}
              {description && (
                // eslint-disable-next-line react/no-danger -- sanitized via DOMPurify in sanitizeHtml() above
                <div className="student-homework-desc" dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }} />
              )}
              {teacherName && <p className="student-homework-teacher">By {teacherName}</p>}
              {attachmentUrl && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  icon={<FaPaperclip />}
                  onClick={() => window.open(attachmentUrl, "_blank", "noopener,noreferrer")}
                >
                  View Attachment
                </Button>
              )}
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
};

export default StudentHomework;
